import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Link2, Plus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Switch from '../ui/Switch';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

// Share links for a video asset, a footage clip or a script, on src/ui (HD-5 Qual): a "Share" button opens a Modal that
// lists the links (copy, view stats, allow comments / allow download, deactivate, delete) and generates new ones.
// A drop-in for every caller (Qual, AiProduction, Production, RawFootage, the Scripts editor): same props, same calls:
// GET <listPath>, POST <base>, PATCH <base>/:id, DELETE <base>/:id. The Modal is portalled to <body> so a legacy
// page's container cannot clip it.
// open / onOpenChange (optional): the caller opens the dialog itself (the Scripts editor's Export menu); no button renders.

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Per-resource wiring: everything that differs between a video share link and a footage share link.
const RESOURCES = {
  video: {
    base:        '/api/share-links',
    listPath:    id => `/api/share-links/video/${id}`,
    createBody:  id => ({ videoAssetId: id }),
    openLabel:   'Share video',
    showComments: true,
    // Video share creation is not gated on isExternal upstream; left as it was for the existing callers.
    internalOnly: false,
  },
  footage: {
    base:        '/api/footage-share-links',
    listPath:    id => `/api/footage-share-links/footage/${id}`,
    createBody:  id => ({ footageId: id }),
    openLabel:   'Share clip',
    // Footage has no comment model, so the switch is absent rather than present and inert.
    showComments: false,
    // The API refuses external users on every footage share route; the control is hidden so it never 403s.
    internalOnly: true,
  },
  // HD-7: the Scripts editor's share links, the three calls the old editor made: GET by-script, POST, DELETE (which
  // deactivates). No PATCH and no settings; the list holds active links only. The old editor showed Share to every
  // role that can open a script, so no role gate here; the API scopes each call to the scripts the caller may see.
  script: {
    base:         '/api/share-links/scripts',
    listPath:     id => `/api/share-links/scripts/by-script/${id}`,
    createBody:   id => ({ scriptId: id }),
    openLabel:    'Share script',
    noun:         'script',
    anyRole:      true,
    reloadAfterCreate: true,   // as before: POST, then GET the list again
    deleteDeactivates: true,
  },
};

export default function ShareLinkManager({
  videoAssetId,
  resourceType = 'video',
  resourceId,
  // Set when the resource cannot be shared right now (e.g. a cold-archived clip). Existing links stay manageable;
  // generating a new one is disabled with this as the reason.
  disabledReason = null,
  open: openProp,
  onOpenChange,
}) {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [openState,  setOpenState]  = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? !!openProp : openState;
  const setOpen = v => (controlled ? onOpenChange?.(v) : setOpenState(v));
  const [links,      setLinks]      = useState([]);
  const [loaded,     setLoaded]     = useState(false);
  const [generating, setGenerating] = useState(false);

  const cfg = RESOURCES[resourceType] || RESOURCES.video;
  // videoAssetId is the pre-generalisation prop name, still honoured.
  const id  = resourceId ?? videoAssetId;

  const hasRole = cfg.anyRole || user?.roles?.some(r => ['Admin', 'Power User', 'Editor', 'Post Lead', 'Post_Lead'].includes(r));
  const canManage = hasRole && !(cfg.internalOnly && user?.isExternal);

  useEffect(() => {
    if (!open || !canManage || !id) return;
    setLoaded(false);
    api.get(cfg.listPath(id))
      .then(r => setLinks(r.data))
      .catch(() => toast('The share links did not load. Close this and try again.', { tone: 'danger' }))
      .finally(() => setLoaded(true));
    // cfg is derived from resourceType, which is in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, canManage, resourceType]);

  if (!canManage || !id) return null;

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await api.post(cfg.base, cfg.createBody(id));
      if (cfg.reloadAfterCreate) {
        const r = await api.get(cfg.listPath(id));
        setLinks(r.data);
      } else {
        setLinks(prev => [res.data, ...prev]);
      }
      toast('Share link created.');
    } catch (err) {
      // The server's reason is the useful message here (e.g. "archived to Storage, restore it first").
      toast(clean(err?.response?.data?.error) || 'The share link was not created. Try again.', { tone: 'danger' });
    } finally {
      setGenerating(false);
    }
  }

  async function handlePatch(link, change) {
    try {
      const res = await api.patch(`${cfg.base}/${link.id}`, change);
      setLinks(prev => prev.map(l => (l.id === link.id ? { ...l, ...res.data } : l)));
    } catch (err) {
      toast(clean(err?.response?.data?.error) || 'The share link did not update. Try again.', { tone: 'danger' });
    }
  }

  async function handleDelete(link) {
    const ok = await confirm(cfg.deleteDeactivates ? {
      title: 'Deactivate this share link?',
      body: 'Anyone with the link loses access to it. This cannot be undone.',
      confirmLabel: 'Deactivate link',
      destructive: true,
    } : {
      title: 'Delete this share link?',
      body: 'Anyone with the link loses access to it. This cannot be undone.',
      confirmLabel: 'Delete link',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`${cfg.base}/${link.id}`);
      setLinks(prev => prev.filter(l => l.id !== link.id));
      toast(cfg.deleteDeactivates ? 'Share link deactivated.' : 'Share link deleted.');
    } catch (err) {
      toast(clean(err?.response?.data?.error) || `The share link was not ${cfg.deleteDeactivates ? 'deactivated' : 'deleted'}. Try again.`, { tone: 'danger' });
    }
  }

  function handleCopy(link) {
    navigator.clipboard.writeText(link.url)
      .then(() => toast('Link copied.'))
      .catch(() => toast('The link was not copied. Select it and copy it by hand.', { tone: 'danger' }));
  }

  return (
    <>
      {!controlled && <Button variant="secondary" icon={Link2} onClick={() => setOpen(true)}>{cfg.openLabel}</Button>}
      {open && createPortal(
        <Modal open onClose={() => setOpen(false)} title="Share links"
          footer={(
            <>
              <Button variant="quiet" onClick={() => setOpen(false)}>Close</Button>
              <Button variant="primary" icon={Plus} loading={generating} disabled={!!disabledReason} onClick={handleGenerate}
                title={disabledReason || undefined}>
                Create share link
              </Button>
            </>
          )}>
          <div className="ui-stack">
            {disabledReason && <p className="ui-p ui-dense ui-text-2">{clean(disabledReason)}</p>}
            {loaded && links.length === 0 && (
              <p className="ui-p ui-dense ui-text-2">No share links yet. Create one to send this {cfg.noun || (resourceType === 'footage' ? 'clip' : 'video')} to someone outside the team.</p>
            )}
            {links.map(link => (cfg.deleteDeactivates
              ? <ScriptShareLinkRow key={link.id} link={link} onCopy={handleCopy} onDeactivate={handleDelete} />
              : <ShareLinkRow key={link.id} link={link} showComments={cfg.showComments}
                  onCopy={handleCopy} onPatch={handlePatch} onDelete={handleDelete} />
            ))}
          </div>
        </Modal>,
        document.body,
      )}
    </>
  );
}

function ShareLinkRow({ link, showComments, onCopy, onPatch, onDelete }) {
  const views = link.viewCount === 0 ? 'Never viewed' : `Viewed ${link.viewCount} time${link.viewCount !== 1 ? 's' : ''}`;
  return (
    <div className={`ui-region ui-region--pad ui-stack${link.isActive ? '' : ' ui-text-3'}`}>
      <div className="ui-cluster">
        {!link.isActive && <Tag>Inactive</Tag>}
        <span className="ui-tc ui-truncate" title={link.url}>{link.url}</span>
        <Button variant="quiet" icon={Copy} onClick={() => onCopy(link)}>Copy</Button>
      </div>
      <span className="ui-meta">{views}{link.lastViewedAt ? `, last viewed ${timeAgo(link.lastViewedAt)}` : ''}</span>
      <div className="ui-cluster">
        {showComments && (
          <Switch label="Allow comments" checked={!!link.allowComments} onChange={e => onPatch(link, { allowComments: e.target.checked })} />
        )}
        <Switch label="Allow download" checked={!!link.allowDownload} onChange={e => onPatch(link, { allowDownload: e.target.checked })} />
      </div>
      <div className="ui-cluster">
        <Button variant="secondary" onClick={() => onPatch(link, { isActive: !link.isActive })}>{link.isActive ? 'Deactivate' : 'Reactivate'}</Button>
        <Button variant="danger" icon={Trash2} onClick={() => onDelete(link)}>Delete</Button>
      </div>
    </div>
  );
}

// A script share link: active links only (the API lists no others), no settings; Deactivate is the DELETE call.
function ScriptShareLinkRow({ link, onCopy, onDeactivate }) {
  const views = !link.viewCount ? 'Never viewed' : `Viewed ${link.viewCount} time${link.viewCount !== 1 ? 's' : ''}`;
  return (
    <div className="ui-region ui-region--pad ui-stack">
      <div className="ui-cluster">
        <span className="ui-tc ui-truncate" title={link.url}>{link.url}</span>
        <Button variant="quiet" icon={Copy} onClick={() => onCopy(link)}>Copy</Button>
      </div>
      <span className="ui-meta">{views}{link.lastViewedAt ? `, last viewed ${timeAgo(link.lastViewedAt)}` : ''}</span>
      <div className="ui-cluster">
        <Button variant="danger" icon={Trash2} onClick={() => onDeactivate(link)}>Deactivate</Button>
      </div>
    </div>
  );
}
